import { useAppSelector, useAppDispatch } from "integrations/redux/hooks"
import { useCallback, useEffect, useState } from "react"
import { SystemInitializingContainer } from "themes/default-tailwind/panels"
import { selectIsProjectLoading } from "features/entity-caching/project/module"
import { flightIdsWithUnsavedFiles } from "@nova/slice-multifile-upload"
import { isLocalDevelopmentEnvironment } from "tools/debugging/module"
import { MapProvider } from "features/map"
import { LeftPanelController } from "./LeftPanelController"
import { LeftPanelConfig, LeftPanelContext } from "./LeftPanelContext"
import { TopBarConfig, TopBarContext } from "./TopBarContext"
import { TopBarController } from "./TopBarController"
import { useJobStatusListener } from "panels/controls/useJobStatusListener"
import { BottomBarConfig, BottomBarContext } from "./BottomBarContext"
import { BottomBarController } from "./BottomBarController"
import { selectColorFinderIsOpen } from "plugins/ColorFinder/V2/redux/selectors"
import { ColorFinderPanel } from "plugins/ColorFinder/V2/ColorFinderPanel"
import { resetBearingAction } from "features/map/control/slice"
import { MapAreaSelector } from "features/map/MapAreaSelector"
import { useProjectInitializer } from "./initializers"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { selectUserID } from "user/profile/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import Page403 from "./Page403"
import { BannerContainer } from "themes/default-tailwind/notifications/BannerContainer"
import { Outlet } from "react-router"

export const ProjectGate = ({ isSnapshot }: { isSnapshot?: boolean }) => {
    const { unauthorized, ready } = useProjectInitializer()
    const userId = useAppSelector(selectUserID)!
    const orgId = useAppSelector(selectActiveWorkspaceId())!
    const projectId = useAppSelector(selectActivatedFireId)!
    fileManagementApi.useCredentialManager({ userId, orgId, projectId })
    const projectIsLoading = useAppSelector(selectIsProjectLoading)
    if (unauthorized) {
        return <Page403 />
    }
    if ((projectIsLoading || !ready) && !isSnapshot) {
        return <SystemInitializingContainer />
    }
    return <Outlet />
}

export const MapRoute = ({ isSnapshot }: { isSnapshot?: boolean }) => {
    useJobStatusListener()
    const dispatch = useAppDispatch()
    const isColorFinderOpen = useAppSelector(selectColorFinderIsOpen)

    // Prevent browser navigation while there are staged/staging/uploading files.
    useEffect(() => {
        if (isLocalDevelopmentEnvironment()) return
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (flightIdsWithUnsavedFiles.size > 0) {
                event.preventDefault()
            }
        }
        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload)
        }
    }, [])

    const [leftPanelConfig, setLeftPanelConfig] = useState<LeftPanelConfig>()
    const [topBarConfig, setTopBarConfig] = useState<TopBarConfig>()
    const [bottomBarConfig, setBottomBarConfig] = useState<BottomBarConfig>()

    const unregisterLeftPanel = useCallback((key: string) => {
        setLeftPanelConfig((prev) => {
            if (prev?.key === key) {
                prev.cleanup?.()
                return undefined
            }
            return prev
        })
    }, [])

    const registerLeftPanel = useCallback(
        (config: LeftPanelConfig) => {
            setLeftPanelConfig((prev) => {
                if (prev?.key === config.key) {
                    return prev
                }
                if (prev?.key) {
                    unregisterLeftPanel(prev.key)
                }
                return config
            })
        },
        [unregisterLeftPanel]
    )

    const unregisterTopBar = useCallback((key: string) => {
        setTopBarConfig((prev) => {
            if (prev?.key === key) {
                prev.cleanup?.()
                return undefined
            }
            return prev
        })
    }, [])
    const registerTopBar = useCallback(
        (config: TopBarConfig) => {
            setTopBarConfig((prev) => {
                if (prev?.key === config.key) {
                    return prev
                }
                if (prev?.key) {
                    unregisterTopBar(prev.key)
                }
                return config
            })
        },
        [unregisterTopBar]
    )

    const unregisterBottomBar = useCallback((key: string) => {
        setBottomBarConfig((prev) => {
            if (prev?.key === key) {
                prev.cleanup?.()
                return undefined
            }
            return prev
        })
    }, [])
    const registerBottomBar = useCallback(
        (config: TopBarConfig) => {
            setBottomBarConfig((prev) => {
                if (prev?.key === config.key) {
                    return prev
                }
                if (prev?.key) {
                    unregisterBottomBar(prev.key)
                }
                return config
            })
        },
        [unregisterBottomBar]
    )

    useEffect(() => {
        if (!isColorFinderOpen) {
            dispatch(resetBearingAction())
        }
    }, [dispatch, isColorFinderOpen])

    return (
        <LeftPanelContext.Provider
            value={{ register: registerLeftPanel, unregister: unregisterLeftPanel }}
        >
            <TopBarContext.Provider
                value={{ register: registerTopBar, unregister: unregisterTopBar }}
            >
                <BottomBarContext.Provider value={{ registerBottomBar, unregisterBottomBar }}>
                    <div className="flex flex-col h-[100vh] w-[100vw] overflow-hidden bg-gray-900">
                        <BannerContainer />
                        <TopBarController config={topBarConfig} />
                        {isColorFinderOpen ? (
                            <ColorFinderPanel map={<MapProvider />} />
                        ) : (
                            <div className="flex-1 min-h-0 overflow-hidden w-full px-2 pb-2">
                                <div className="w-full h-full flex relative border border-white-200 rounded-lg overflow-hidden">
                                    <LeftPanelController config={leftPanelConfig} />
                                    <div className="relative w-full">
                                        {isSnapshot ? <MapAreaSelector /> : <MapProvider />}
                                        <BottomBarController config={bottomBarConfig} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </BottomBarContext.Provider>
            </TopBarContext.Provider>
        </LeftPanelContext.Provider>
    )
}
